import { Grid } from '@mui/material'
import { PDFViewer } from '@react-pdf/renderer'
import isFunction from 'lodash/isFunction'
import { useMemo } from 'react'
import {
  AutocompleteArrayInput,
  FormDataConsumer,
  type Identifier,
  ReferenceArrayInput,
  SelectInput,
  SimpleForm,
  TextInput,
  useGetMany,
  useRecordContext,
} from 'react-admin'
import { useWatch } from 'react-hook-form'
import { BrokerageAgreementDoc, CreateCustomer } from '~/components'
import { customerName, type JoinField, toJoinRows, withJoinIds } from '~/helpers'
// import './UpsertDocument.css'
import { documentTypeChoices } from '~/types/choices'
import { DefaultAccessor, documentDefinitions } from '~/types/documents'

const DOCUMENT_JOINS: JoinField[] = [
  { rows: 'document_customer', key: 'customer_id', ids: 'customer_ids' },
  { rows: 'document_boat', key: 'boat_id', ids: 'boat_ids' },
]

/** The Create/Edit `transform`: the form's id lists back into join rows. */
export const documentRelations = toJoinRows(DOCUMENT_JOINS)

/** The form for a document, opened with id lists for its customers and boats. */
export const DocumentForm = ({ showPreview }: { showPreview: boolean }) => {
  const record = useRecordContext()
  const formRecord = useMemo(() => withJoinIds(record, DOCUMENT_JOINS), [record])
  return (
    <SimpleForm record={formRecord} warnWhenUnsavedChanges>
      <UpsertDocument showPreview={showPreview} />
    </SimpleForm>
  )
}

const UpsertDocument = ({ showPreview }: { showPreview: boolean }) => {
  const values = useWatch()
  const customerIds: Identifier[] = values.customer_ids ?? []
  const { data: customers } = useGetMany('customers', { ids: customerIds })
  const boatIds: Identifier[] = values.boat_ids ?? []
  const { data: boats } = useGetMany('boats', { ids: boatIds })

  const handleTypeChange = (_event: any) => {
    // if (target.value) {
    //   setValue('title', null)
    // }
  }

  return (
    <Grid container spacing={2}>
      <Grid size={{ md: 2 }}>
        <SelectInput
          source='type'
          choices={documentTypeChoices}
          label='resources.documents.fields.type'
          fullWidth
          onChange={handleTypeChange}
        />
      </Grid>
      <Grid size={{ md: 5 }}>
        <ReferenceArrayInput
          source='customer_ids'
          reference='customers'
          enableGetChoices={({ q }) => (q ? q.length >= 1 : false)}
        >
          <AutocompleteArrayInput
            label='resources.documents.fields.document_customer'
            optionText={(record) =>
              record && record.id === '@@ra-create' ? record.name : customerName(record)
            }
            shouldRenderSuggestions={(value) => value.length >= 1}
            create={<CreateCustomer />}
          />
        </ReferenceArrayInput>
      </Grid>
      <Grid size={{ md: 5 }}>
        <ReferenceArrayInput
          source='boat_ids'
          reference='boats'
          enableGetChoices={({ q }) => (q ? q.length >= 1 : false)}
        >
          <AutocompleteArrayInput
            label='resources.documents.fields.document_boat'
            optionText={(record) => record && `${record.model} (${record.name})`}
            shouldRenderSuggestions={(value) => value.length >= 1}
          />
        </ReferenceArrayInput>
      </Grid>

      <Grid size={{ md: showPreview ? 6 : 12 }}>
        <Grid container spacing={1}>
          <FormDataConsumer>
            {({ formData }) => {
              const docDef = formData.type ? documentDefinitions[formData.type] : null
              if (!docDef) return null
              return Object.keys(docDef.fields).map((k) => {
                const fieldDef = docDef.fields[k]
                const defaultSources = { customers: customers ?? [], boats: boats ?? [] }
                const defaultValue = isFunction(fieldDef.default)
                  ? (fieldDef.default as DefaultAccessor)(defaultSources)
                  : fieldDef.default
                return (
                  <Grid size={{ md: showPreview ? 6 : 3 }} key={k}>
                    <TextInput
                      source={`values.${k}`}
                      label={`resources.documents.${formData.type}.fields.${k}`}
                      placeholder={defaultValue}
                      slotProps={{ inputLabel: { shrink: !!defaultValue || undefined } }}
                    />
                  </Grid>
                )
              })
            }}
          </FormDataConsumer>
        </Grid>
      </Grid>
      {showPreview && values.type === 'brokerage_agreement' && (
        <Grid size={{ md: 6 }}>
          <PDFViewer
            style={{ width: '100%', height: 'auto', minHeight: '650px', maxHeight: '1000px' }}
          >
            <BrokerageAgreementDoc record={values} defaultSources={{ customers, boats }} />
          </PDFViewer>
        </Grid>
      )}

      {/* <Grid size={{ md: 6 }}>
        <FormDataConsumer>
          {({ formData }) => (
            <TextInput
              source='title'
              fullWidth
              label='resources.documents.fields.title'
              validate={validateTitle}
              disabled={!!formData.type}
            />
          )}
        </FormDataConsumer>
      </Grid> */}
      {/* <Grid size={{ md: 12 }}>
        <TextInput multiline source='notes' fullWidth label='resources.documents.fields.notes' />
      </Grid> */}
    </Grid>
  )
}

// TODO: fill and use interface
export interface DocumentDefinition {}

export default UpsertDocument
